// [S-095c] Tenant security policy data hooks.
//
// Backs the Settings → Security section.
// GET surfaces the policy (returns a default shape when no doc exists
// yet so the form always has something to render). PUT replaces the
// policy atomically — there's no partial-update endpoint by design,
// since the surface is small (3 fields) and an audit event fires on
// every write.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type SsoProvider = "okta" | "azure" | "google";
export type AuditRetentionDays = 365 | 1095 | 2555;

export type SecurityPolicyRow = {
  readonly tenantId: string;
  readonly ssoProvider: SsoProvider | null;
  readonly mfaRequired: boolean;
  readonly auditRetentionDays: AuditRetentionDays;
  readonly updatedAt: string;
  readonly updatedBy: string;
};

export type UpdateSecurityPolicyInput = {
  ssoProvider: SsoProvider | null;
  mfaRequired: boolean;
  auditRetentionDays: AuditRetentionDays;
};

export const SECURITY_POLICY_KEY = ["admin", "tenant-security"] as const;

export function useSecurityPolicy(
  tenantId: string
): UseQueryResult<SecurityPolicyRow, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...SECURITY_POLICY_KEY, "detail", tenantId],
    queryFn: () =>
      api<SecurityPolicyRow>(
        `/tenants/${encodeURIComponent(tenantId)}/security`
      ),
    enabled: tenantId.length > 0,
  });
}

export function useUpdateSecurityPolicy(
  tenantId: string
): UseMutationResult<SecurityPolicyRow, Error, UpdateSecurityPolicyInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<SecurityPolicyRow>(
        `/tenants/${encodeURIComponent(tenantId)}/security`,
        { method: "PUT", body: input }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...SECURITY_POLICY_KEY, "detail", tenantId],
      });
    },
  });
}
